import { useState, useEffect, useRef } from "react";

const SmoothCursor = () => {
    const mousePosition = useRef({ x: 0, y: 0 });
    const dotPosition = useRef({ x: 0, y: 0 });
    const borderDotPosition = useRef({ x: 0, y: 0 });

    const [renderPos, setRenderPos] = useState({
        dot: { x: 0, y: 0 },
        border: { x: 0, y: 0 }
    });
    const [isHovering, setIsHovering] = useState(false);

    const DOT_SMOOTHNESS = 0.2;
    const BORDER_DOT_SMOOTHNESS = 0.1;

    useEffect(() => {
        const handleMouseMove = (e) => {
            mousePosition.current = { x: e.clientX, y: e.clientY };
        };

        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        // Add event listeners
        window.addEventListener("mousemove", handleMouseMove);

        const interactiveElements = document.querySelectorAll(
            "a, button, img, input, textarea, select"
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        // Animation function for smooth movement
        const animate = () => {
            const lerp = (start, end, factor) => {
                return start + (end - start) * factor;
            };

            dotPosition.current.x = lerp(
                dotPosition.current.x,
                mousePosition.current.x,
                DOT_SMOOTHNESS
            );
            dotPosition.current.y = lerp(
                dotPosition.current.y,
                mousePosition.current.y,
                DOT_SMOOTHNESS
            );

            borderDotPosition.current.x = lerp(
                borderDotPosition.current.x,
                mousePosition.current.x,
                BORDER_DOT_SMOOTHNESS
            );
            borderDotPosition.current.y = lerp(
                borderDotPosition.current.y,
                mousePosition.current.y,
                BORDER_DOT_SMOOTHNESS
            );

            setRenderPos({
                dot: { x: dotPosition.current.x, y: dotPosition.current.y },
                border: { x: borderDotPosition.current.x, y: borderDotPosition.current.y },
            });

            requestAnimationFrame(animate);
        };

        // Start animation loop
        const animationId = requestAnimationFrame(animate);

        // Clean up
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);

            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });

            cancelAnimationFrame(animationId);
        };
    }, []);

    return (
        <div className="pointer-events-none fixed inset-0 z-[9999] hidden md:block">
            {/* Inner Dot with gradient and shadow for visibility */}
            <div
                className="absolute rounded-full"
                style={{
                    width: "10px",
                    height: "10px",
                    background: "linear-gradient(135deg, #eab308, #f97316)",
                    transform: "translate(-50%, -50%)",
                    left: `${renderPos.dot.x}px`,
                    top: `${renderPos.dot.y}px`,
                    boxShadow: "0 0 12px rgba(234, 179, 8, 0.9), 0 0 24px rgba(249, 115, 22, 0.5)",
                }}
            />

            {/* Outer Border Circle with dual ring for contrast */}
            <div
                className="absolute rounded-full"
                style={{
                    width: isHovering ? "44px" : "28px",
                    height: isHovering ? "44px" : "28px",
                    transform: "translate(-50%, -50%)",
                    left: `${renderPos.border.x}px`,
                    top: `${renderPos.border.y}px`,
                    border: "2px solid rgba(234, 179, 8, 0.7)",
                    background: "rgba(234, 179, 8, 0.08)",
                    backdropFilter: "blur(2px)",
                    boxShadow: "0 0 18px rgba(234, 179, 8, 0.5), inset 0 0 12px rgba(249, 115, 22, 0.15)",
                    transition: "width 0.3s ease-out, height 0.3s ease-out, border-color 0.3s ease-out",
                    borderColor: isHovering ? "rgba(249, 115, 22, 0.9)" : "rgba(234, 179, 8, 0.7)",
                }}
            />

            {/* Additional outer glow for white backgrounds */}
            <div
                className="absolute rounded-full"
                style={{
                    width: isHovering ? "48px" : "32px",
                    height: isHovering ? "48px" : "32px",
                    transform: "translate(-50%, -50%)",
                    left: `${renderPos.border.x}px`,
                    top: `${renderPos.border.y}px`,
                    border: "1px solid rgba(255, 255, 255, 0.4)",
                    transition: "width 0.3s ease-out, height 0.3s ease-out",
                }}
            />
        </div>
    );
};

export default SmoothCursor;
